import { useParams, Link } from "react-router-dom";
import { SEOHead, SITE_URL } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BLOG_POSTS } from "@/data/blogs";
import { Calendar, User, Clock, Share2, MessageSquare, ArrowLeft, CheckCircle2, Tag, ArrowRight, ShieldCheck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import NotFound from "./NotFound";

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((b) => b.slug === slug);
  if (!post) return <><SEOHead title="Rehber bulunamadı" noindex /><NotFound /></>;

  // Find related posts in the same category or random fallback
  const relatedPosts = BLOG_POSTS.filter(
    (b) => b.slug !== post.slug && (b.category === post.category || b.tags.some((t) => post.tags.includes(t)))
  ).slice(0, 3);

  // Fallback if not enough category matches
  const displayedRelated = relatedPosts.length >= 3 ? relatedPosts : BLOG_POSTS.filter((b) => b.slug !== post.slug).slice(0, 3);

  // Article JSON-LD Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.summary,
    "image": new URL(post.image, SITE_URL).href,
    "author": {
      "@type": "Organization",
      "name": post.author,
      "url": `${SITE_URL}/`
    },
    "publisher": {
      "@type": "Organization",
      "name": "Corner Gayrimenkul",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.cornergayrimenkul.com.tr/favicon.ico"
      }
    },
    "datePublished": post.publishDate,
    "dateModified": post.lastUpdated,
    "mainEntityOfPage": `${SITE_URL}/blog/${post.slug}`,
    "inLanguage": "tr",
    "citation": post.sources.map((source) => source.url)
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.summary,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Bağlantı kopyalandı!");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title={`${post.title} - Corner Gayrimenkul Blog`}
        description={post.summary}
        canonicalPath={`/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        schemaJson={articleSchema}
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto w-full">
        <Breadcrumbs
          items={[
            { label: "Blog & Emlak Rehberi", url: "/blog" },
            { label: post.title, url: `/blog/${post.slug}` }
          ]}
        />

        <article className="space-y-8 my-6">
          {/* Header Metadata */}
          <div className="space-y-4 text-center sm:text-left">
            <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start">
              <span className="px-3.5 py-1 rounded-full bg-primary/10 text-primary font-black text-xs uppercase tracking-wider">
                {post.category}
              </span>
              <span className="px-3.5 py-1 rounded-full bg-muted text-muted-foreground font-semibold text-xs flex items-center gap-1">
                <Clock className="h-3 w-3" /> {post.readTime}
              </span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
              {post.title}
            </h1>

            <p className="text-muted-foreground text-base sm:text-xl leading-relaxed">
              {post.summary}
            </p>

            <div className="flex flex-wrap items-center justify-between gap-4 border-y border-border py-4 text-xs sm:text-sm text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5 font-bold text-foreground">
                  <User className="h-4 w-4 text-primary" /> {post.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-muted-foreground" /> İçerik kontrolü: <time dateTime={post.lastUpdated}>6 Ekim 2026</time>
                </span>
              </div>

              <Button onClick={handleShare} variant="outline" size="sm" className="font-bold gap-2">
                <Share2 className="h-4 w-4" /> Paylaş
              </Button>
            </div>
          </div>

          {/* Hero Article Visual Image */}
          <figure><div className="relative aspect-[16/9] overflow-hidden rounded-3xl border border-border shadow-xl bg-muted"><img src={post.image} alt={`${post.title} için temsili görsel`} className="w-full h-full object-cover" /></div><figcaption className="text-xs text-muted-foreground mt-2">Temsili görsel</figcaption></figure>

          {/* Layout Container with Main Article & Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-4">
            {/* Main Article Content */}
            <div className="lg:col-span-8 space-y-6">
              {/* Quick Summary / Highlights Box */}
              <div className="bg-muted/60 p-6 rounded-2xl border border-border space-y-3">
                <h3 className="font-extrabold text-sm uppercase tracking-wider text-primary flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> Rehber Özet Bilgileri
                </h3>
                <ul className="space-y-2 text-sm text-foreground/90 font-medium">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <span>Mülk ziyaretini ve işlem hazırlığını planlamak için pratik kontrol bilgileri.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <span>Hizmet ve belge bilgileri için yazı sonunda resmî kaynak bağlantıları.</span>
                  </li>
                </ul>
              </div>

              <section className="rounded-2xl border border-border bg-muted/30 p-6" aria-label="Yazının kaynakları">
                <h2 className="text-lg font-extrabold mb-3">Resmî kaynaklar ve içerik kapsamı</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">Bu yazı, mülk arayışı ve işlem hazırlığı için editoryal bir rehberdir. İlgili hizmet ve belge bilgilerinin kapsamını aşağıdaki resmî kaynaklardan inceleyebilirsiniz. İşleme özel bilgileri ilgili kurumdan doğrulayın.</p>
                <div className="flex flex-col gap-3">{post.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex gap-2 items-center text-sm text-primary underline underline-offset-2">{source.title}<ExternalLink className="h-3.5 w-3.5 shrink-0" /></a>)}</div>
              </section>

              {/* Body Paragraphs */}
              <div className="prose prose-slate max-w-none text-foreground text-base sm:text-lg leading-relaxed space-y-6">
                {post.content.map((paragraph, index) => {
                  // If paragraph starts with numbers or bold structure like "1.", make heading
                  if (/^\d+\./.test(paragraph)) {
                    return (
                      <div key={index} className="space-y-2 pt-4">
                        <h3 className="text-xl sm:text-2xl font-black text-foreground border-l-4 border-primary pl-4 py-1">
                          {paragraph.split(":")[0]}
                        </h3>
                        {paragraph.includes(":") && (
                          <p className="text-foreground/90 pl-4">{paragraph.split(":").slice(1).join(":")}</p>
                        )}
                      </div>
                    );
                  }
                  return (
                    <p key={index} className="text-foreground/90 leading-relaxed">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* Article Tags */}
              <div className="pt-6 border-t border-border flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-muted-foreground mr-2 flex items-center gap-1">
                  <Tag className="h-3.5 w-3.5" /> Etiketler:
                </span>
                {post.tags.map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-muted rounded-full text-xs font-semibold text-foreground/80 border border-border/60">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Author Bio Box */}
              <div className="bg-card p-6 rounded-2xl border border-border shadow-sm flex items-center gap-4 my-8">
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center text-primary font-black text-xl shrink-0">
                  {post.author.charAt(0)}
                </div>
                <div className="space-y-1">
                  <h4 className="font-extrabold text-base text-foreground">{post.author}</h4>
                  <p className="text-xs text-muted-foreground">Gayrimenkul bilgi rehberi</p>
                  <p className="text-xs text-foreground/80">Mülk ziyaretine ve işlem hazırlığına yönelik editoryal kontrol listeleri.</p>
                </div>
              </div>
            </div>

            {/* Sidebar CTA & Contact */}
            <div className="lg:col-span-4 space-y-6">
              <div className="sticky top-24 space-y-6">
                {/* CTA Box */}
                <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-6 rounded-2xl shadow-xl space-y-4">
                  <div className="space-y-2">
                    <span className="px-2.5 py-1 rounded-md bg-white/20 text-white font-extrabold text-[10px] uppercase">
                      Ücretsiz Danışmanlık
                    </span>
                    <h3 className="text-xl font-black leading-snug text-white">
                      Adana'da Ev Almayı veya Satmayı mı Düşünüyorsunuz?
                    </h3>
                    <p className="text-xs text-white/90 leading-relaxed">
                      İlçenizi, mülk tipini ve ihtiyaçlarınızı paylaşın; arayışınızı birlikte planlayalım.
                    </p>
                  </div>

                  <div className="space-y-2 pt-2">
                    <Button asChild size="lg" className="w-full font-bold bg-white text-primary hover:bg-white/90 shadow-md">
                      <a href={`https://wa.me/905335487801?text=${encodeURIComponent("Merhaba, rehber yazınızı inceledim ve gayrimenkul danışmanlığı almak istiyorum.")}`} target="_blank" rel="noopener noreferrer">
                        <MessageSquare className="h-4 w-4 mr-2 text-green-600" /> WhatsApp'tan Yazın
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Back to Blog Button */}
                <Button asChild variant="outline" className="w-full font-bold rounded-xl">
                  <Link to="/blog">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Tüm Blog Yazılarına Dön
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="pt-12 border-t border-border space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-black text-foreground">İlginizi Çekebilecek Diğer Rehberler</h3>
                <p className="text-sm text-muted-foreground">Benzer konulardaki kaynaklı kontrol rehberleri</p>
              </div>
              <Link to="/blog" className="text-xs font-bold text-primary hover:underline flex items-center gap-1">
                Tümünü Gör <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {displayedRelated.map((rel) => (
                <Card key={rel.id} className="overflow-hidden group hover:shadow-lg transition-all duration-300 border border-border/80 bg-card rounded-2xl flex flex-col justify-between">
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img src={rel.image} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 px-2.5 py-0.5 text-[10px] font-bold rounded-full bg-primary text-primary-foreground">
                      {rel.category}
                    </span>
                  </div>
                  <CardContent className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                    <h4 className="font-extrabold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      <Link to={`/blog/${rel.slug}`}>{rel.title}</Link>
                    </h4>
                    <Link to={`/blog/${rel.slug}`} className="inline-flex items-center text-xs font-bold text-primary hover:underline pt-2 border-t border-border/60">
                      Oku <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </article>
      </main>

      <FooterSEO />
    </div>
  );
}
